const SHAPE_TYPE_ID_TO_NAME = {
  7000: 'point',
  7001: 'sphere',
  7002: 'cylinder',
  7003: 'box',
  7004: 'polygon',
  7005: 'path',
  7006: 'polyhedron',
  7007: 'heightfield',
};

const PLACE_OVERVIEW_CONTEXT_BUFFER_RATIO = 0.1;
const PLACE_OVERVIEW_CONTEXT_BUFFER_MIN = 50;
const PLACE_OVERVIEW_CONTEXT_BUFFER_MAX = 2000;
const PLACE_OVERVIEW_COLORS = [
  { fill: '#d9ecff', stroke: '#3b82c4' },
  { fill: '#e5f6df', stroke: '#5d9d44' },
  { fill: '#fbe5d6', stroke: '#c77f3f' },
  { fill: '#f3e4ff', stroke: '#8a63c7' },
  { fill: '#ffe2e0', stroke: '#d26767' },
  { fill: '#efe8d5', stroke: '#8e7a49' },
];
const NATION_OVERVIEW_COLORS = [
  { fill: '#fca5a5', stroke: '#b91c1c' },
  { fill: '#d8b4fe', stroke: '#7e22ce' },
  { fill: '#fdba74', stroke: '#c2410c' },
  { fill: '#fde68a', stroke: '#b45309' },
  { fill: '#f9a8d4', stroke: '#be185d' },
  { fill: '#bbf7d0', stroke: '#15803d' },
  { fill: '#fecdd3', stroke: '#be123c' },
  { fill: '#ddd6fe', stroke: '#6d28d9' },
];
const FIXED_PLACE_OVERVIEW_COLORS = [
  { match: 'passage', fill: '#000000', stroke: '#000000', preserveStroke: true },
  { match: 'water feature', fill: '#bfdbfe', stroke: '#64748b' },
  { match: 'ocean', fill: '#bfdbfe', stroke: '#64748b' },
  { match: 'sea', fill: '#bfdbfe', stroke: '#64748b' },
  { match: 'lake', fill: '#bfdbfe', stroke: '#64748b' },
  { match: 'mountain range', fill: '#5a3514', stroke: '#2f1b0a' },
  { match: 'mountains', fill: '#6b3f18', stroke: '#3b220d' },
  { match: 'hills', fill: '#c89b63', stroke: '#8a5f2c' },
  { match: 'forest', fill: '#14532d', stroke: '#052e16' },
  { match: 'desert', fill: '#f2d28b', stroke: '#b8872f' },
];

const normalizeShapeTypeName = (shapeType) => {
  const normalized = String(shapeType ?? '').trim().toLowerCase();
  return SHAPE_TYPE_ID_TO_NAME[Number(normalized)] ?? normalized;
};

const parseShapeData = (value) => {
  try { return typeof value === 'string' ? JSON.parse(value) || {} : value || {}; }
  catch { return {}; }
};

const getShapeBounds = (place) => {
  const shape = place?.shape ?? {};
  const location = place?.location ?? {};
  const shapeType = normalizeShapeTypeName(shape.shapeType);
  const data = parseShapeData(shape.shapeData);
  if (shapeType === 'path' && Array.isArray(data.paths) && data.paths.flat().length) {
    const bounds = getPointBounds(data.paths.flat());
    return inflateBounds(bounds, (Number(data.width) || 20) / 2);
  }
  const shapePoints = getShapePoints(place);

  if (['polygon', 'path'].includes(shapeType) && shapePoints.length >= 2) {
    return getPointBounds(shapePoints);
  }

  const radius = Math.max(10, Number(location.maxRadius) || 10);
  const centerX = Number(location.x) || 0;
  const centerZ = Number(location.z) || 0;
  const minX = Number(shape.minX);
  const maxX = Number(shape.maxX);
  const minZ = Number(shape.minZ);
  const maxZ = Number(shape.maxZ);

  if ([minX, maxX, minZ, maxZ].every((value) => Number.isFinite(value))) {
    return { minX, maxX, minZ, maxZ };
  }

  return {
    minX: centerX - radius,
    maxX: centerX + radius,
    minZ: centerZ - radius,
    maxZ: centerZ + radius,
  };
};

const getOverviewDescriptorText = (place) => [
  place?.placeType,
  place?.zoneType,
  place?.buildingType,
  place?.transitPointType,
  place?.name,
  place?.title,
  place?.entityName,
  place?.entityTitle,
].map((value) => String(value ?? '').trim().toLowerCase()).join(' ');

const LINEAR_OVERVIEW_DESCRIPTOR_PATTERN =
  /\b(road|roads|river|rivers|street|streets|route|routes|trail|trails|highway|highways|passage|passages|path|paths)\b/;

const isRoadOrRiverOverviewPlace = (place) =>
  LINEAR_OVERVIEW_DESCRIPTOR_PATTERN.test(getOverviewDescriptorText(place));

const getOverviewRenderLayer = (place, color = null) => {
  const shapeType = normalizeShapeTypeName(place?.shape?.shapeType);

  if (color?.preserveStroke || shapeType === 'path') {
    return 2;
  }

  return 0;
};

const getOverviewColor = (place, nationColorIndex = null) => {
  const placeType = { 8007: 'passage', 8010: 'water feature', 8029: 'river' }[place?.placeType] || String(place?.placeType ?? '').trim().toLowerCase();
  const shapeType = normalizeShapeTypeName(place?.shape?.shapeType);
  const role = String(place?.mapRole || '').toLowerCase();
  const overviewTypeText = getOverviewDescriptorText(place);
  const isRiver = (['river', 'stream', 'waterway', 'canal', 'water feature'].includes(placeType) ||
    (placeType !== 'passage' && role === 'river') ||
    (shapeType === 'path' && placeType !== 'passage' && role !== 'road' && /\b(river|stream|waterway|canal)\b/.test(overviewTypeText))) && shapeType === 'path';

  if (isRiver) {
    return { fill: 'none', stroke: '#2563eb', preserveStroke: true };
  }

  if (role === 'road' || placeType === 'passage' || (shapeType === 'path' && isRoadOrRiverOverviewPlace(place))) {
    return FIXED_PLACE_OVERVIEW_COLORS[0];
  }
  // A settlement called Waytown, Seabrook or Forest Gate remains a settlement.
  const typeText = [placeType, place?.zoneType, place?.buildingType].filter(Boolean).join(' ').toLowerCase();
  const colorText = !placeType || ['natural area', 'landmark'].includes(placeType) ? overviewTypeText : typeText;
  const fixedColor = FIXED_PLACE_OVERVIEW_COLORS.find((entry) => new RegExp(`\\b${entry.match}\\b`).test(colorText));

  if (fixedColor) {
    return fixedColor;
  }

  if (placeType.includes('nation') && Number.isInteger(nationColorIndex)) {
    return NATION_OVERVIEW_COLORS[nationColorIndex % NATION_OVERVIEW_COLORS.length];
  }

  const key = String(
    placeType.includes('nation')
      ? place?.idEntity || place?.name || place?.title || place?.entityName || place?.entityTitle || 'nation'
      : place?.zoneType || place?.buildingType || place?.placeType || 'unknown'
  ).toLowerCase();
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }

  if (placeType.includes('nation')) {
    return NATION_OVERVIEW_COLORS[hash % NATION_OVERVIEW_COLORS.length];
  }

  return PLACE_OVERVIEW_COLORS[hash % PLACE_OVERVIEW_COLORS.length];
};

const getShapePoints = (place) => {
  const shape = place?.shape ?? {};
  const shapeType = normalizeShapeTypeName(shape.shapeType);
  const shapeData = parseShapeData(shape.shapeData);
  const points = Array.isArray(shapeData.points)
    ? shapeData.points
    : Array.isArray(shapeData.vertices)
      ? shapeData.vertices
      : [];

  if (!['polygon', 'path'].includes(shapeType) || points.length < 2) {
    return [];
  }

  return points
    .filter((point) => point?.x !== null && point?.x !== undefined && point?.x !== '' &&
      point?.z !== null && point?.z !== undefined && point?.z !== '')
    .map((point) => ({
      x: Number(point?.x),
      z: Number(point?.z),
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.z));
};

const getBoxShapePoints = (place) => {
  const bounds = getShapeBounds(place);

  if (![bounds.minX, bounds.maxX, bounds.minZ, bounds.maxZ].every((value) => Number.isFinite(Number(value)))) {
    return [];
  }

  return [
    { x: bounds.minX, z: bounds.minZ },
    { x: bounds.maxX, z: bounds.minZ },
    { x: bounds.maxX, z: bounds.maxZ },
    { x: bounds.minX, z: bounds.maxZ },
  ];
};

const isRectangularOverviewShape = (shapeType) =>
  shapeType === 'box' || !['polygon', 'path', 'sphere', 'cylinder'].includes(shapeType);

const getOverviewShapePoints = (place) => {
  const shapeType = normalizeShapeTypeName(place?.shape?.shapeType);

  if (isRectangularOverviewShape(shapeType)) {
    return getBoxShapePoints(place);
  }

  return getShapePoints(place);
};

// Separate SVG subpaths keep gates open; never draw a connecting segment
// between independent physical wall sections.
export const getMultipartPathData = (place, toX = x => x, toZ = z => z) => {
  const data = parseShapeData(place?.shape?.shapeData);
  if (normalizeShapeTypeName(place?.shape?.shapeType) !== 'path' || !Array.isArray(data.paths)) return '';
  return data.paths.filter(points => Array.isArray(points) && points.length >= 2).map(points =>
    points.map((point, index) => `${index ? 'L' : 'M'}${toX(Number(point.x))},${toZ(Number(point.z))}`).join(' ')).join(' ');
};

const getPointBounds = (points) =>
  points.reduce(
    (bounds, point) => ({
      minX: Math.min(bounds.minX, point.x),
      maxX: Math.max(bounds.maxX, point.x),
      minZ: Math.min(bounds.minZ, point.z),
      maxZ: Math.max(bounds.maxZ, point.z),
    }),
    { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity }
  );

const boundsOverlap = (left, right) =>
  left.minX <= right.maxX &&
  left.maxX >= right.minX &&
  left.minZ <= right.maxZ &&
  left.maxZ >= right.minZ;

const inflateBounds = (bounds, amount) => ({
  minX: bounds.minX - amount,
  maxX: bounds.maxX + amount,
  minZ: bounds.minZ - amount,
  maxZ: bounds.maxZ + amount,
});

const getOverviewContextBuffer = (bounds) => {
  const width = Math.max(1, Number(bounds?.maxX) - Number(bounds?.minX));
  const height = Math.max(1, Number(bounds?.maxZ) - Number(bounds?.minZ));
  const scaledBuffer = Math.max(width, height) * PLACE_OVERVIEW_CONTEXT_BUFFER_RATIO;

  return Math.max(
    PLACE_OVERVIEW_CONTEXT_BUFFER_MIN,
    Math.min(PLACE_OVERVIEW_CONTEXT_BUFFER_MAX, scaledBuffer)
  );
};

const isPointInBounds = (point, bounds) =>
  point.x >= bounds.minX &&
  point.x <= bounds.maxX &&
  point.z >= bounds.minZ &&
  point.z <= bounds.maxZ;

const getOverviewFootprintPoints = (place) => parseShapeData(place?.shape?.shapeData).paths?.flat() || getOverviewShapePoints(place);

const getOverviewSegments = (place) => {
  const data = parseShapeData(place?.shape?.shapeData);
  if (normalizeShapeTypeName(place?.shape?.shapeType) === 'path' && Array.isArray(data.paths)) return data.paths.flatMap(points =>
    getOverviewSegments({ ...place, shape: { ...place.shape, shapeData: { width: data.width, points } } }));
  const shapeType = normalizeShapeTypeName(place?.shape?.shapeType);
  const points = getOverviewFootprintPoints(place);
  const isClosed = isRectangularOverviewShape(shapeType) || shapeType === 'polygon';

  if (points.length < 2) {
    return [];
  }

  return points
    .map((point, index) => {
      const nextPoint = points[index + 1] ?? (isClosed ? points[0] : null);
      return nextPoint ? { from: point, to: nextPoint } : null;
    })
    .filter(Boolean);
};

const getSegmentOrientation = (a, b, c) => {
  const value = (b.z - a.z) * (c.x - b.x) - (b.x - a.x) * (c.z - b.z);

  if (Math.abs(value) < 0.000001) {
    return 0;
  }

  return value > 0 ? 1 : 2;
};

const isPointOnSegment = (point, segment) =>
  point.x <= Math.max(segment.from.x, segment.to.x) + 0.000001 &&
  point.x >= Math.min(segment.from.x, segment.to.x) - 0.000001 &&
  point.z <= Math.max(segment.from.z, segment.to.z) + 0.000001 &&
  point.z >= Math.min(segment.from.z, segment.to.z) - 0.000001 &&
  getSegmentOrientation(segment.from, segment.to, point) === 0;

const segmentsIntersect = (left, right) => {
  const o1 = getSegmentOrientation(left.from, left.to, right.from);
  const o2 = getSegmentOrientation(left.from, left.to, right.to);
  const o3 = getSegmentOrientation(right.from, right.to, left.from);
  const o4 = getSegmentOrientation(right.from, right.to, left.to);

  if (o1 !== o2 && o3 !== o4) {
    return true;
  }

  return (
    (o1 === 0 && isPointOnSegment(right.from, left)) ||
    (o2 === 0 && isPointOnSegment(right.to, left)) ||
    (o3 === 0 && isPointOnSegment(left.from, right)) ||
    (o4 === 0 && isPointOnSegment(left.to, right))
  );
};

const isPointInPolygon = (point, polygonPoints) => {
  if (!Array.isArray(polygonPoints) || polygonPoints.length < 3) {
    return false;
  }

  let isInside = false;
  for (let index = 0, previousIndex = polygonPoints.length - 1; index < polygonPoints.length; previousIndex = index, index += 1) {
    const current = polygonPoints[index];
    const previous = polygonPoints[previousIndex];
    const segment = { from: previous, to: current };

    if (isPointOnSegment(point, segment)) {
      return true;
    }

    const crosses =
      (current.z > point.z) !== (previous.z > point.z) &&
      point.x < ((previous.x - current.x) * (point.z - current.z)) / (previous.z - current.z) + current.x;

    if (crosses) {
      isInside = !isInside;
    }
  }

  return isInside;
};

const isClosedOverviewShape = (place) => {
  const shapeType = normalizeShapeTypeName(place?.shape?.shapeType);
  return isRectangularOverviewShape(shapeType) || shapeType === 'polygon';
};

const placesIntersectForOverview = (left, right) => {
  if (!left || !right) {
    return false;
  }

  const leftBounds = getShapeBounds(left);
  const rightBounds = getShapeBounds(right);

  if (!boundsOverlap(leftBounds, rightBounds)) {
    return false;
  }

  const leftSegments = getOverviewSegments(left);
  const rightSegments = getOverviewSegments(right);

  if (leftSegments.length === 0 || rightSegments.length === 0) {
    return true;
  }

  if (leftSegments.some((leftSegment) => rightSegments.some((rightSegment) => segmentsIntersect(leftSegment, rightSegment)))) {
    return true;
  }

  const leftPoints = getOverviewFootprintPoints(left);
  const rightPoints = getOverviewFootprintPoints(right);

  if (isClosedOverviewShape(left) && rightPoints.some((point) => isPointInPolygon(point, leftPoints))) {
    return true;
  }

  return isClosedOverviewShape(right) && leftPoints.some((point) => isPointInPolygon(point, rightPoints));
};

const getBoundsSegments = (bounds) => {
  const corners = [
    { x: bounds.minX, z: bounds.minZ },
    { x: bounds.maxX, z: bounds.minZ },
    { x: bounds.maxX, z: bounds.maxZ },
    { x: bounds.minX, z: bounds.maxZ },
  ];

  return corners.map((corner, index) => ({
    from: corner,
    to: corners[(index + 1) % corners.length],
  }));
};

const segmentIntersectsBounds = (segment, bounds) =>
  isPointInBounds(segment.from, bounds) ||
  isPointInBounds(segment.to, bounds) ||
  getBoundsSegments(bounds).some((boundsSegment) => segmentsIntersect(segment, boundsSegment));

const placeIsNearForOverview = (focusPlace, candidatePlace) => {
  if (!focusPlace || !candidatePlace) {
    return false;
  }

  const focusBounds = getShapeBounds(focusPlace);
  const candidateBounds = getShapeBounds(candidatePlace);
  const bufferedBounds = inflateBounds(focusBounds, getOverviewContextBuffer(focusBounds));

  if (!boundsOverlap(bufferedBounds, candidateBounds)) {
    return false;
  }

  const candidateSegments = getOverviewSegments(candidatePlace);

  if (candidateSegments.length === 0) {
    return true;
  }

  return candidateSegments.some((segment) => segmentIntersectsBounds(segment, bufferedBounds));
};

const collectOverviewContextPlaces = ({
  place,
  directChildIds = [],
  nestedChildren = [],
  expansionFocusPlace = null,
  parentContextPlaces = [],
  spatialContextPlaces = [],
}) => {
  const contextFocusPlaces = [
    place,
    ...(expansionFocusPlace ? [expansionFocusPlace] : []),
  ];
  const containedIds = new Set([
    place?.idEntity,
    ...directChildIds,
    ...nestedChildren.map((child) => child?.idEntity).filter(Boolean),
  ].map((idEntity) => String(idEntity ?? '')));
  const contextPlacesById = new Map();

  const candidates = spatialContextPlaces.some(entry => entry.imageMapContext) ? spatialContextPlaces : [
    ...(Array.isArray(place?.siblingPlaces) ? place.siblingPlaces : []),
    ...(Array.isArray(place?.adjacentPlaces) ? place.adjacentPlaces : []),
    ...(Array.isArray(parentContextPlaces) ? parentContextPlaces : []),
    ...(Array.isArray(spatialContextPlaces) ? spatialContextPlaces : []),
  ];
  candidates.filter((candidate) => {
      const candidateId = String(candidate?.idEntity ?? '');
      const boundaryPoints = parseShapeData(candidate?.shape?.shapeData).boundaryPoints;
      const enclosure = Array.isArray(boundaryPoints) && boundaryPoints.length >= 3
        ? { ...candidate, shape: { shapeType: 'Polygon', shapeData: { points: boundaryPoints } } } : null;
      return (
        candidateId &&
        !containedIds.has(candidateId) &&
        (candidate.imageMapContext || !(candidate.composedOfParentIds || []).some(parentId => containedIds.has(String(parentId)))) &&
        (candidate.imageMapContext || contextFocusPlaces.some((contextFocusPlace) =>
          placesIntersectForOverview(contextFocusPlace, candidate) ||
          placeIsNearForOverview(contextFocusPlace, candidate) ||
          (enclosure && placesIntersectForOverview(contextFocusPlace, enclosure))
        ))
      );
    })
    .forEach((candidate) => contextPlacesById.set(candidate.idEntity, candidate));

  return [...contextPlacesById.values()];
};

export { normalizeShapeTypeName, getShapeBounds, getShapePoints, getBoxShapePoints,
  isRectangularOverviewShape, getOverviewShapePoints, getOverviewRenderLayer,
  getOverviewColor, getPointBounds, collectOverviewContextPlaces, getOverviewContextBuffer };
